import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Plus,
  RotateCcw,
  Trash2,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  compile,
  sample,
  toSegments,
  autoYRange,
  findIntersections,
  specialPoints,
  PRESETS,
  DEFAULT_COLORS,
  type FunctionRow,
  type SamplePoint,
  type Viewport,
} from "@/lib/calculators/graphingCalculator";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Graphing Calculator", bn: "গ্রাফিং ক্যালকুলেটর" },
  desc: {
    en: "Plot multiple functions, zoom and pan, find intersections — a free Desmos-style graphing tool for SSC/HSC math.",
    bn: "একাধিক ফাংশনের গ্রাফ আঁকুন, জুম-প্যান করুন, ছেদবিন্দু খুঁজুন — SSC/HSC গণিতের জন্য Desmos-স্টাইল গ্রাফিং টুল।",
  },
  functions: { en: "Functions", bn: "ফাংশন" },
  add: { en: "Add", bn: "যোগ করুন" },
  presets: { en: "Presets", bn: "প্রিসেট" },
  view: { en: "View", bn: "ভিউ" },
  xRange: { en: "X range", bn: "X-পরিসর" },
  yRange: { en: "Y range", bn: "Y-পরিসর" },
  auto: { en: "Auto", bn: "অটো" },
  resolution: { en: "Resolution", bn: "রেজল্যুশন" },
  grid: { en: "Grid", bn: "গ্রিড" },
  axes: { en: "Axes", bn: "অক্ষ" },
  labels: { en: "Labels", bn: "লেবেল" },
  trigMode: { en: "Trig mode", bn: "ত্রিকোণমিতি" },
  reset: { en: "Reset view", bn: "রিসেট" },
  zoomIn: { en: "Zoom in", bn: "জুম ইন" },
  zoomOut: { en: "Zoom out", bn: "জুম আউট" },
  exportPng: { en: "Export PNG", bn: "PNG ডাউনলোড" },
  print: { en: "Print", bn: "প্রিন্ট" },
  parameters: { en: "Parameters", bn: "প্যারামিটার" },
  specialPoints: { en: "Special points", bn: "বিশেষ বিন্দু" },
  zeros: { en: "Zeros (x-intercepts)", bn: "শূন্যবিন্দু (X অক্ষ)" },
  yIntercept: { en: "Y-intercept", bn: "Y অক্ষ ছেদ" },
  extrema: { en: "Local extrema", bn: "স্থানীয় চরমমান" },
  intersections: { en: "Intersections", bn: "ছেদবিন্দু" },
  none: { en: "None in current view", bn: "এই ভিউতে নেই" },
  bnDigits: { en: "Bengali numerals on axes", bn: "অক্ষে বাংলা সংখ্যা" },
  syntax: { en: "Syntax help", bn: "সিনট্যাক্স" },
  syntaxHelp: {
    en: "Use ^ for power, * for multiply, sqrt(), abs(), sin/cos/tan, log() = log₁₀, ln() = natural log, exp(x) = e^x, pi, e.",
    bn: "ব্যবহার করুন: ^ (পাওয়ার), * (গুণ), sqrt(), abs(), sin/cos/tan, log() = log₁₀, ln() = প্রাকৃতিক, exp(x) = e^x, pi, e.",
  },
  notice: {
    en: "Plots are sampled — not symbolic. Asymptotes and very rapid oscillations may render imperfectly.",
    bn: "গ্রাফ নমুনা থেকে আঁকা — প্রতীকী নয়। অ্যাসিম্পটোট ও দ্রুত দোলন কখনো অসম্পূর্ণ দেখাতে পারে।",
  },
  errorParse: { en: "Cannot parse expression", bn: "এক্সপ্রেশন পার্স করা যায়নি" },
};

type TrigMode = "rad" | "deg";

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How do I plot a graph?", bn: "গ্রাফ কীভাবে আঁকবো?" },
    a: {
      en: "Type any function of x in the input — for example x^2, sin(x), or 2x + 3. The graph updates automatically. Add up to 6 functions to overlay them and compare.",
      bn: "ইনপুটে x-এর যেকোনো ফাংশন লিখুন — যেমন x^2, sin(x), বা 2x + 3। গ্রাফ স্বয়ংক্রিয়ভাবে আপডেট হবে। তুলনার জন্য সর্বোচ্চ ৬টি ফাংশন একসাথে দেখাতে পারেন।",
    },
  },
  {
    q: { en: "What is the function syntax?", bn: "ফাংশনের সিনট্যাক্স কী?" },
    a: {
      en: "Use standard math: + − * /, ^ for power, sqrt() for square root, abs() for absolute value, sin/cos/tan for trig, log() for log base 10, ln() for natural log, exp(x) for e^x, pi for π, e for Euler's number.",
      bn: "সাধারণ গণিত: + − * /, পাওয়ার ^, বর্গমূল sqrt(), পরম abs(), ত্রিকোণমিতি sin/cos/tan, log() = log১০, ln() = প্রাকৃতিক, exp(x) = e^x, pi = π, e = অয়লার সংখ্যা।",
    },
  },
  {
    q: { en: "How do I find intersections of two curves?", bn: "দুই বক্ররেখার ছেদ কীভাবে পাব?" },
    a: {
      en: "Add two functions. The 'Intersections' panel below the graph lists approximate crossing points within the current X range. Zoom in for higher precision.",
      bn: "দুটি ফাংশন যোগ করুন। গ্রাফের নিচে 'ছেদবিন্দু' প্যানেলে বর্তমান X-পরিসরের ছেদের আনুমানিক স্থানাঙ্ক দেখানো হবে। নির্ভুলতা বাড়াতে জুম করুন।",
    },
  },
  {
    q: { en: "How do I zoom and pan?", bn: "জুম ও প্যান কীভাবে?" },
    a: {
      en: "Drag with mouse or finger to pan. Scroll wheel zooms on desktop, pinch zoom works on touch devices. Use the +/− buttons or 'Reset view' to return to default.",
      bn: "মাউস বা আঙ্গুলে টেনে প্যান করুন। ডেস্কটপে স্ক্রল করে জুম, টাচ ডিভাইসে দুই আঙ্গুলে পিঞ্চ। +/− বাটন বা 'রিসেট' দিয়ে ডিফল্টে ফিরুন।",
    },
  },
  {
    q: { en: "Can I save the graph?", bn: "গ্রাফ কি সেভ করা যায়?" },
    a: {
      en: "Yes — use 'Export PNG' to download the current plot as an image, or 'Print' for a paper copy. Function definitions are kept in your browser and reload when you return.",
      bn: "হ্যাঁ — 'PNG ডাউনলোড' দিয়ে বর্তমান গ্রাফ ইমেজ হিসেবে সেভ করুন, বা 'প্রিন্ট' করুন। ফাংশনের সংজ্ঞা ব্রাউজারে সংরক্ষিত থাকে।",
    },
  },
  {
    q: { en: "গ্রাফ কীভাবে আঁকবো?", bn: "গ্রাফ কীভাবে আঁকবো?" },
    a: {
      en: "ইনপুট বক্সে y = x^2 বা sin(x) এর মত যেকোনো ফাংশন লিখুন। গ্রাফ সাথে সাথেই দেখাবে। SSC/HSC এর জন্য প্রিসেট গ্যালারি থেকে দ্রুত শুরু করতে পারেন।",
      bn: "ইনপুট বক্সে y = x^2 বা sin(x) এর মত যেকোনো ফাংশন লিখুন। গ্রাফ সাথে সাথেই দেখাবে। SSC/HSC এর জন্য প্রিসেট গ্যালারি থেকে দ্রুত শুরু করতে পারেন।",
    },
  },
];

const STORAGE_KEY = "blcalc:graphing:v1";

export const Route = createFileRoute("/graphing-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Graphing Calculator — Plot Functions, Free",
      description:
        "Free graphing calculator. Plot multiple functions, zoom, trace, find intersections. Desmos-style for SSC/HSC math in Bangladesh. গ্রাফিং ক্যালকুলেটর।",
      path: "/graphing-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Graphing Calculator", path: "/graphing-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: GraphingCalculatorPage,
});

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

const DEFAULT_FUNCS: FunctionRow[] = [
  { id: uid(), expr: "x^2", color: DEFAULT_COLORS[0], visible: true },
];

const DEFAULT_VIEWPORT: Viewport = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 };

interface UsesParams {
  a: boolean;
  b: boolean;
  c: boolean;
}

function detectParams(rows: FunctionRow[]): UsesParams {
  const all = rows.map((r) => r.expr).join(" ");
  return {
    a: /\ba\b/.test(all),
    b: /\bb\b/.test(all),
    c: /\bc\b/.test(all),
  };
}

function GraphingCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  const [rows, setRows] = useState<FunctionRow[]>(DEFAULT_FUNCS);
  const [viewport, setViewport] = useState<Viewport>(DEFAULT_VIEWPORT);
  const [autoY, setAutoY] = useState(true);
  const [resolution, setResolution] = useState(500);
  const [showGrid, setShowGrid] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [trigMode, setTrigMode] = useState<TrigMode>("rad");
  const [bnDigits, setBnDigits] = useState(false);
  const [params, setParams] = useState({ a: 1, b: 0, c: 0 });
  const [tracePoints, setTracePoints] = useState<{ x: number; y: number; color: string }[]>([]);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [debouncedRows, setDebouncedRows] = useState<FunctionRow[]>(DEFAULT_FUNCS);

  // Debounce rows for evaluation
  useEffect(() => {
    const id = setTimeout(() => setDebouncedRows(rows), 250);
    return () => clearTimeout(id);
  }, [rows]);

  // Persist
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (Array.isArray(data.rows) && data.rows.length) {
          setRows(data.rows);
          setDebouncedRows(data.rows);
        }
        if (data.viewport) setViewport(data.viewport);
        if (typeof data.autoY === "boolean") setAutoY(data.autoY);
        if (data.params) setParams(data.params);
        if (data.trigMode) setTrigMode(data.trigMode);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ rows, viewport, autoY, params, trigMode }),
      );
    } catch {}
  }, [rows, viewport, autoY, params, trigMode]);

  const usesParams = useMemo(() => detectParams(debouncedRows), [debouncedRows]);

  const compiled = useMemo(
    () => debouncedRows.map((r) => ({ row: r, fn: compile(r.expr, params) })),
    [debouncedRows, params],
  );

  const sampled = useMemo(() => {
    return compiled.map(({ row, fn }) => ({
      row,
      fn,
      points: fn.ok && row.visible
        ? sample(fn, { xMin: viewport.xMin, xMax: viewport.xMax, resolution, trigMode })
        : ([] as SamplePoint[]),
    }));
  }, [compiled, viewport.xMin, viewport.xMax, resolution, trigMode]);

  const effectiveYRange = useMemo(() => {
    if (!autoY) return { yMin: viewport.yMin, yMax: viewport.yMax };
    const allPts = sampled.map((s) => s.points);
    return autoYRange(allPts, viewport.xMax - viewport.xMin);
  }, [autoY, sampled, viewport]);

  const effectiveViewport: Viewport = {
    xMin: viewport.xMin,
    xMax: viewport.xMax,
    yMin: effectiveYRange.yMin,
    yMax: effectiveYRange.yMax,
  };

  // Special points
  const specials = useMemo(() => {
    return sampled
      .filter((s) => s.fn.ok && s.row.visible && s.points.length)
      .map((s) => ({ row: s.row, special: specialPoints(s.fn, s.points, trigMode) }));
  }, [sampled, trigMode]);

  // Intersections (pairwise of first 4 visible)
  const intersections = useMemo(() => {
    const visible = sampled.filter((s) => s.fn.ok && s.row.visible && s.points.length);
    const out: { i: number; j: number; pts: { x: number; y: number }[] }[] = [];
    for (let i = 0; i < visible.length; i++) {
      for (let j = i + 1; j < visible.length; j++) {
        const pts = findIntersections(visible[i].points, visible[j].points);
        if (pts.length) {
          out.push({
            i: debouncedRows.indexOf(visible[i].row),
            j: debouncedRows.indexOf(visible[j].row),
            pts,
          });
        }
      }
    }
    return out;
  }, [sampled, debouncedRows]);

  // ---- Function row mutators ----
  const updateRow = (id: string, patch: Partial<FunctionRow>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    if (rows.length >= 6) return;
    setRows((rs) => [
      ...rs,
      { id: uid(), expr: "", color: DEFAULT_COLORS[rs.length % DEFAULT_COLORS.length], visible: true },
    ]);
  };
  const removeRow = (id: string) => setRows((rs) => rs.filter((r) => r.id !== id));

  const usePreset = (expr: string) => {
    if (rows.length === 0) {
      setRows([{ id: uid(), expr, color: DEFAULT_COLORS[0], visible: true }]);
      return;
    }
    // replace last empty, else add
    const empty = rows.find((r) => !r.expr.trim());
    if (empty) {
      updateRow(empty.id, { expr });
    } else if (rows.length < 6) {
      setRows((rs) => [
        ...rs,
        { id: uid(), expr, color: DEFAULT_COLORS[rs.length % DEFAULT_COLORS.length], visible: true },
      ]);
    } else {
      // overwrite first
      updateRow(rows[0].id, { expr });
    }
  };

  // ---- Plot interaction ----
  const resetView = () => {
    setViewport(DEFAULT_VIEWPORT);
    setAutoY(true);
    setTracePoints([]);
  };

  const zoomBy = (factor: number) => {
    setAutoY(false);
    setViewport((v) => {
      const cx = (v.xMin + v.xMax) / 2;
      const cy = ((effectiveViewport.yMin) + (effectiveViewport.yMax)) / 2;
      const w = (v.xMax - v.xMin) * factor;
      const h = (effectiveViewport.yMax - effectiveViewport.yMin) * factor;
      return {
        xMin: cx - w / 2,
        xMax: cx + w / 2,
        yMin: cy - h / 2,
        yMax: cy + h / 2,
      };
    });
  };

  const fmt = (n: number, digits = 3) => {
    let s = Number.isFinite(n) ? Number(n.toFixed(digits)).toString() : "—";
    if (bnDigits) s = toBengaliNumerals(s);
    return s;
  };

  return (
    <CalculatorLayout
      slug="graphing-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="LineChart"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Notice */}
      <div className={cn(
        "mb-4 flex items-start gap-2 rounded-xl border border-blue-400/30 bg-blue-400/5 p-3 text-xs text-foreground/80",
        lang === "bn" && "font-bn",
      )}>
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-blue-500" />
        <p>{tr("notice")}</p>
      </div>

      {/* Function input rows */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <label className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("functions")}</label>
          <Button variant="outline" size="sm" onClick={addRow} disabled={rows.length >= 6}>
            <Plus size={14} className="mr-1" /> {tr("add")}
          </Button>
        </div>
        <div className="space-y-1.5">
          {rows.map((row, i) => {
            const c = compiled.find((c) => c.row.id === row.id);
            const error = c && !c.fn.ok && row.expr.trim();
            return (
              <div key={row.id} className="flex items-center gap-1.5">
                <input
                  aria-label={`function color ${i + 1}`}
                  type="color"
                  value={row.color}
                  onChange={(e) => updateRow(row.id, { color: e.target.value })}
                  className="h-9 w-9 cursor-pointer rounded border border-border bg-card p-0.5"
                />
                <button
                  type="button"
                  onClick={() => updateRow(row.id, { visible: !row.visible })}
                  className={cn(
                    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border bg-card text-muted-foreground transition hover:bg-muted",
                    !row.visible && "opacity-40",
                  )}
                  aria-label={row.visible ? "hide" : "show"}
                >
                  {row.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <span className="hidden shrink-0 text-sm text-muted-foreground sm:inline">y =</span>
                <input
                  type="text"
                  value={row.expr}
                  onChange={(e) => updateRow(row.id, { expr: e.target.value })}
                  placeholder="x^2"
                  spellCheck={false}
                  className={cn(
                    "h-9 w-full rounded-md border bg-card px-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/60",
                    error ? "border-destructive" : "border-border",
                  )}
                />
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length === 1}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border bg-card text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
                  aria-label="remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
        {compiled.some((c) => !c.fn.ok && c.row.expr.trim()) && (
          <p className="text-xs text-destructive">⚠ {tr("errorParse")}</p>
        )}
        <p className={cn("text-[11px] text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("syntaxHelp")}
        </p>
      </section>

      {/* Plot */}
      <PlotArea
        viewport={effectiveViewport}
        setViewport={(v) => { setAutoY(false); setViewport(v); }}
        sampled={sampled}
        showGrid={showGrid}
        showLabels={showLabels}
        bnDigits={bnDigits}
        tracePoints={tracePoints}
        setTracePoints={setTracePoints}
        onHover={setHover}
        intersections={intersections}
      />

      {/* Toolbar under plot */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={() => zoomBy(0.7)}>
          <ZoomIn size={14} className="mr-1" /> {tr("zoomIn")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => zoomBy(1.4)}>
          <ZoomOut size={14} className="mr-1" /> {tr("zoomOut")}
        </Button>
        <Button variant="outline" size="sm" onClick={resetView}>
          <RotateCcw size={14} className="mr-1" /> {tr("reset")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => exportPng(plotCanvasRef.current)}>
          <Download size={14} className="mr-1" /> {tr("exportPng")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => typeof window !== "undefined" && window.print()}>
          <Printer size={14} className="mr-1" /> {tr("print")}
        </Button>
        {hover && (
          <span className="ml-auto rounded bg-muted px-2 py-1 font-mono text-xs">
            ({fmt(hover.x)}, {fmt(hover.y)})
          </span>
        )}
      </div>

      {/* View controls */}
      <section className="mt-5 grid grid-cols-1 gap-3 rounded-xl border border-border bg-card/50 p-3 sm:grid-cols-2">
        <div>
          <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("xRange")}</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={viewport.xMin}
              onChange={(e) => setViewport((v) => ({ ...v, xMin: Number(e.target.value) }))}
              className="h-8 w-full rounded-md border border-border bg-card px-2 text-sm"
            />
            <span className="text-muted-foreground">→</span>
            <input
              type="number"
              value={viewport.xMax}
              onChange={(e) => setViewport((v) => ({ ...v, xMax: Number(e.target.value) }))}
              className="h-8 w-full rounded-md border border-border bg-card px-2 text-sm"
            />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className={cn("block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("yRange")}</label>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={autoY} onChange={(e) => setAutoY(e.target.checked)} className="accent-accent" />
              <span className={lang === "bn" ? "font-bn" : ""}>{tr("auto")}</span>
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              disabled={autoY}
              value={Number(effectiveViewport.yMin.toFixed(3))}
              onChange={(e) => setViewport((v) => ({ ...v, yMin: Number(e.target.value) }))}
              className="h-8 w-full rounded-md border border-border bg-card px-2 text-sm disabled:opacity-50"
            />
            <span className="text-muted-foreground">→</span>
            <input
              type="number"
              disabled={autoY}
              value={Number(effectiveViewport.yMax.toFixed(3))}
              onChange={(e) => setViewport((v) => ({ ...v, yMax: Number(e.target.value) }))}
              className="h-8 w-full rounded-md border border-border bg-card px-2 text-sm disabled:opacity-50"
            />
          </div>
        </div>
        <div>
          <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("resolution")}: {resolution}
          </label>
          <input
            type="range"
            min={100}
            max={2000}
            step={100}
            value={resolution}
            onChange={(e) => setResolution(Number(e.target.value))}
            className="w-full accent-accent"
          />
        </div>
        <div className="flex flex-wrap items-end gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} className="accent-accent" />
            <span className={lang === "bn" ? "font-bn" : ""}>{tr("grid")}</span>
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} className="accent-accent" />
            <span className={lang === "bn" ? "font-bn" : ""}>{tr("labels")}</span>
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="accent-accent" />
            <span className={lang === "bn" ? "font-bn" : ""}>{tr("bnDigits")}</span>
          </label>
          <div className="flex items-center gap-1.5">
            <span className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("trigMode")}:</span>
            <button
              type="button"
              onClick={() => setTrigMode("rad")}
              className={cn(
                "rounded border px-2 py-0.5 text-xs",
                trigMode === "rad" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card",
              )}
            >RAD</button>
            <button
              type="button"
              onClick={() => setTrigMode("deg")}
              className={cn(
                "rounded border px-2 py-0.5 text-xs",
                trigMode === "deg" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card",
              )}
            >DEG</button>
          </div>
        </div>
      </section>

      {/* Parameter sliders */}
      {(usesParams.a || usesParams.b || usesParams.c) && (
        <section className="mt-4 rounded-xl border border-border bg-card/50 p-3">
          <h3 className={cn("mb-2 text-sm font-medium", lang === "bn" && "font-bn")}>{tr("parameters")}</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(["a", "b", "c"] as const).map((k) =>
              usesParams[k] ? (
                <div key={k}>
                  <label className="mb-1 block text-xs text-muted-foreground">{k} = {fmt(params[k], 2)}</label>
                  <input
                    type="range"
                    min={-10}
                    max={10}
                    step={0.1}
                    value={params[k]}
                    onChange={(e) => setParams((p) => ({ ...p, [k]: Number(e.target.value) }))}
                    className="w-full accent-accent"
                  />
                </div>
              ) : null,
            )}
          </div>
        </section>
      )}

      {/* Presets */}
      <section className="mt-4">
        <h3 className={cn("mb-2 text-sm font-medium", lang === "bn" && "font-bn")}>{tr("presets")}</h3>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => usePreset(p.expr)}
              className="rounded-full border border-border bg-card px-3 py-1 font-mono text-xs text-foreground transition hover:border-accent hover:bg-accent/10"
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      {/* Special points */}
      <section className="mt-5">
        <h3 className={cn("mb-2 text-sm font-medium", lang === "bn" && "font-bn")}>{tr("specialPoints")}</h3>
        <div className="space-y-3">
          {specials.length === 0 && (
            <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("none")}</p>
          )}
          {specials.map(({ row, special }, idx) => (
            <motion.div
              key={row.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              className="rounded-lg border border-border bg-card p-3"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: row.color }} />
                <code className="font-mono text-xs">y = {row.expr}</code>
              </div>
              <dl className="grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-3">
                <div>
                  <dt className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("yIntercept")}</dt>
                  <dd className="font-mono">{special.yIntercept !== null ? `(0, ${fmt(special.yIntercept)})` : tr("none")}</dd>
                </div>
                <div>
                  <dt className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("zeros")}</dt>
                  <dd className="font-mono">
                    {special.zeros.length
                      ? special.zeros.slice(0, 5).map((z) => fmt(z)).join(", ")
                      : tr("none")}
                  </dd>
                </div>
                <div>
                  <dt className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("extrema")}</dt>
                  <dd className="font-mono">
                    {special.extrema.length
                      ? special.extrema.slice(0, 4).map((e) => `${e.kind === "max" ? "↑" : "↓"}(${fmt(e.x, 2)}, ${fmt(e.y, 2)})`).join(", ")
                      : tr("none")}
                  </dd>
                </div>
              </dl>
            </motion.div>
          ))}

          {intersections.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-3">
              <h4 className={cn("mb-2 text-xs font-medium", lang === "bn" && "font-bn")}>{tr("intersections")}</h4>
              <ul className="space-y-1 text-xs">
                {intersections.map((it, k) => (
                  <li key={k}>
                    <span className="text-muted-foreground">f{it.i + 1} ∩ f{it.j + 1}:</span>{" "}
                    <span className="font-mono">
                      {it.pts.slice(0, 6).map((p) => `(${fmt(p.x)}, ${fmt(p.y)})`).join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Educational content */}
      <article className={cn("mt-8 space-y-5 text-sm leading-relaxed", lang === "bn" && "font-bn")}>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "ব্যবহার নির্দেশিকা" : "How to use this graphing calculator"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "প্রথম ইনপুটে x-এর যেকোনো ফাংশন লিখুন। 'যোগ করুন' দিয়ে ৬টি পর্যন্ত ফাংশন একসাথে দেখান। প্রতিটির রঙ ও দৃশ্যমানতা পাল্টানো যায়। গ্রাফে টানলে প্যান, স্ক্রল/পিঞ্চ করলে জুম হবে। 'প্রিসেট' থেকে SSC/HSC এর সাধারণ ফাংশন এক ক্লিকে আনুন।"
              : "Type any function of x in the first input. Use 'Add' to overlay up to 6 functions, each with its own colour and visibility toggle. Drag the plot to pan, scroll or pinch to zoom. The 'Presets' panel gives one-click access to the common SSC/HSC functions."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "ফাংশনের সিনট্যাক্স" : "Function input syntax"}</h2>
          <ul className="ml-5 list-disc space-y-1 text-muted-foreground">
            <li>{lang === "bn" ? "পাওয়ার: x^2, x^3" : "Powers: x^2, x^3"}</li>
            <li>{lang === "bn" ? "গুণ: 2*x বা 2x — দুটোই কাজ করে" : "Multiply: 2*x or 2x — both work"}</li>
            <li>sqrt(x), abs(x), exp(x)</li>
            <li>sin(x), cos(x), tan(x), asin(x), acos(x), atan(x)</li>
            <li>{lang === "bn" ? "log(x) মানে log₁₀(x), ln(x) মানে প্রাকৃতিক লগ" : "log(x) is log base 10; ln(x) is natural log"}</li>
            <li>{lang === "bn" ? "ধ্রুবক: pi, e" : "Constants: pi, e"}</li>
          </ul>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "সাধারণ ফাংশনের আকার" : "Common functions and shapes"}</h2>
          <ul className="ml-5 list-disc space-y-1 text-muted-foreground">
            <li>{lang === "bn" ? "y = x² — উপরমুখী প্যারাবোলা" : "y = x² — upward parabola"}</li>
            <li>{lang === "bn" ? "y = x³ — কিউবিক, উৎসে অংশভাগ" : "y = x³ — cubic, passes through origin with inflection"}</li>
            <li>{lang === "bn" ? "y = sin(x) — ২π পর্যায় সাইন তরঙ্গ" : "y = sin(x) — sine wave, period 2π"}</li>
            <li>{lang === "bn" ? "y = e^x — দ্রুত বর্ধনশীল সূচকীয়" : "y = e^x — exponential growth"}</li>
            <li>{lang === "bn" ? "y = 1/x — হাইপারবোলা, অক্ষদ্বয় অ্যাসিম্পটোট" : "y = 1/x — hyperbola, axes are asymptotes"}</li>
          </ul>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "ছেদ, শূন্য ও চরমমান" : "Intersections, zeros, and extrema"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "একই ভিউয়ের মধ্যে বক্ররেখার শূন্যবিন্দু (X অক্ষ), Y অক্ষ ছেদ এবং স্থানীয় চরমমান স্বয়ংক্রিয়ভাবে অনুমান করা হয়। দুটি ফাংশন থাকলে তাদের ছেদবিন্দু আলাদা প্যানেলে দেখানো হয়। সব মান নমুনা ভিত্তিক — নির্ভুলতার জন্য জুম করে রেজল্যুশন বাড়ান।"
              : "Within the current view we approximate each curve's zeros, y-intercept and local extrema. With two or more curves we also list intersection points. All values are sample-based — for finer precision, zoom in or raise the resolution slider."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "SSC শিক্ষার্থীদের জন্য" : "For SSC students"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "রৈখিক ফাংশন (y = mx + c) এবং দ্বিঘাত (y = ax² + bx + c) এর শেপ বুঝতে স্লাইডার দিয়ে p, b, c পাল্টান। প্যারাবোলার শীর্ষবিন্দু কীভাবে সরে — দেখুন। বইয়ের সমীকরণ দিয়ে যাচাই করুন।"
              : "Use the parameter sliders to feel how m, c (linear) or a, b, c (quadratic) reshape the curve. Watch the parabola's vertex shift as you change them, then verify with your textbook problems."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "HSC শিক্ষার্থীদের জন্য" : "For HSC students"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "ত্রিকোণমিতি (sin, cos, tan), লগারিদম, সূচক ও ক্যালকুলাসের ভিজ্যুয়ালাইজেশনে ব্যবহার করুন। ডেরিভেটিভ আঁকতে আলাদা ফাংশন রো-তে নিজে লিখে চাপান — ঢালের পরিবর্তন বোঝা সহজ হবে।"
              : "Useful for trig (sin/cos/tan), logarithmic, exponential, and calculus visualisation. Plot a function and its derivative side-by-side to see how slope corresponds to the original curve."}
          </p>
        </section>
      </article>

      {/* Related */}
      <section className="mt-8">
        <h2 className={cn("mb-3 text-base font-bold", lang === "bn" && "font-bn")}>
          {lang === "bn" ? "সম্পর্কিত ক্যালকুলেটর" : "Related calculators"}
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/scientific-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "সায়েন্টিফিক ক্যালকুলেটর" : "Scientific Calculator"}
          </Link>
          <Link to="/math-solver" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "ম্যাথ সলভার" : "Math Solver"}
          </Link>
        </div>
      </section>
    </CalculatorLayout>
  );
}

// Plot canvas ref shared with toolbar via module-scoped ref (single instance per page)
let plotCanvasRef: { current: HTMLCanvasElement | null } = { current: null };

interface PlotProps {
  viewport: Viewport;
  setViewport: (v: Viewport) => void;
  sampled: { row: FunctionRow; fn: { ok: boolean }; points: SamplePoint[] }[];
  showGrid: boolean;
  showLabels: boolean;
  bnDigits: boolean;
  tracePoints: { x: number; y: number; color: string }[];
  setTracePoints: (pts: { x: number; y: number; color: string }[]) => void;
  onHover: (p: { x: number; y: number } | null) => void;
  intersections: { i: number; j: number; pts: { x: number; y: number }[] }[];
}

function PlotArea({
  viewport,
  setViewport,
  sampled,
  showGrid,
  showLabels,
  bnDigits,
  tracePoints,
  setTracePoints,
  onHover,
  intersections,
}: PlotProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 600, h: 400 });

  useEffect(() => {
    plotCanvasRef.current = canvasRef.current;
    return () => {
      if (plotCanvasRef.current === canvasRef.current) plotCanvasRef.current = null;
    };
  });

  // Resize observer
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.max(280, entry.contentRect.width);
        const h = Math.max(280, Math.min(560, w * 0.7));
        setSize({ w: Math.round(w), h: Math.round(h) });
      }
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // Draw
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    cvs.width = Math.floor(size.w * dpr);
    cvs.height = Math.floor(size.h * dpr);
    cvs.style.width = `${size.w}px`;
    cvs.style.height = `${size.h}px`;
    const ctx = cvs.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawPlot(ctx, size.w, size.h, viewport, sampled, {
      showGrid,
      showLabels,
      bnDigits,
      tracePoints,
      intersections,
    });
  }, [size, viewport, sampled, showGrid, showLabels, bnDigits, tracePoints, intersections]);

  // Coord conversion
  const xToPx = useCallback(
    (x: number) => ((x - viewport.xMin) / (viewport.xMax - viewport.xMin)) * size.w,
    [viewport, size.w],
  );
  const yToPx = useCallback(
    (y: number) => size.h - ((y - viewport.yMin) / (viewport.yMax - viewport.yMin)) * size.h,
    [viewport, size.h],
  );
  const pxToX = useCallback(
    (px: number) => viewport.xMin + (px / size.w) * (viewport.xMax - viewport.xMin),
    [viewport, size.w],
  );
  const pxToY = useCallback(
    (py: number) => viewport.yMin + ((size.h - py) / size.h) * (viewport.yMax - viewport.yMin),
    [viewport, size.h],
  );

  // Mouse: pan + wheel zoom
  const dragRef = useRef<{ x: number; y: number; vx: Viewport } | null>(null);
  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, vx: viewport };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    onHover({ x: pxToX(px), y: pxToY(py) });
    if (dragRef.current) {
      const dx = e.clientX - dragRef.current.x;
      const dy = e.clientY - dragRef.current.y;
      const xRange = dragRef.current.vx.xMax - dragRef.current.vx.xMin;
      const yRange = dragRef.current.vx.yMax - dragRef.current.vx.yMin;
      setViewport({
        xMin: dragRef.current.vx.xMin - (dx / size.w) * xRange,
        xMax: dragRef.current.vx.xMax - (dx / size.w) * xRange,
        yMin: dragRef.current.vx.yMin + (dy / size.h) * yRange,
        yMax: dragRef.current.vx.yMax + (dy / size.h) * yRange,
      });
    }
  };
  const onMouseUp = () => { dragRef.current = null; };
  const onMouseLeave = () => { dragRef.current = null; onHover(null); };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const cx = pxToX(px);
    const cy = pxToY(py);
    const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    setViewport({
      xMin: cx - (cx - viewport.xMin) * factor,
      xMax: cx + (viewport.xMax - cx) * factor,
      yMin: cy - (cy - viewport.yMin) * factor,
      yMax: cy + (viewport.yMax - cy) * factor,
    });
  };

  // Touch: drag + pinch
  const touchRef = useRef<{
    mode: "pan" | "pinch";
    start: Touch[];
    vx: Viewport;
    dist?: number;
    center?: { x: number; y: number };
  } | null>(null);
  type Touch = { x: number; y: number };
  const getTouches = (e: React.TouchEvent): Touch[] => {
    const cvs = canvasRef.current;
    if (!cvs) return [];
    const rect = cvs.getBoundingClientRect();
    return Array.from(e.touches).map((t) => ({ x: t.clientX - rect.left, y: t.clientY - rect.top }));
  };
  const onTouchStart = (e: React.TouchEvent) => {
    const ts = getTouches(e);
    if (ts.length === 1) {
      touchRef.current = { mode: "pan", start: ts, vx: viewport };
    } else if (ts.length === 2) {
      const dx = ts[0].x - ts[1].x, dy = ts[0].y - ts[1].y;
      touchRef.current = {
        mode: "pinch",
        start: ts,
        vx: viewport,
        dist: Math.hypot(dx, dy),
        center: { x: (ts[0].x + ts[1].x) / 2, y: (ts[0].y + ts[1].y) / 2 },
      };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    const ts = getTouches(e);
    const t = touchRef.current;
    if (!t) return;
    if (t.mode === "pan" && ts.length === 1) {
      const dx = ts[0].x - t.start[0].x;
      const dy = ts[0].y - t.start[0].y;
      const xRange = t.vx.xMax - t.vx.xMin;
      const yRange = t.vx.yMax - t.vx.yMin;
      setViewport({
        xMin: t.vx.xMin - (dx / size.w) * xRange,
        xMax: t.vx.xMax - (dx / size.w) * xRange,
        yMin: t.vx.yMin + (dy / size.h) * yRange,
        yMax: t.vx.yMax + (dy / size.h) * yRange,
      });
    } else if (t.mode === "pinch" && ts.length === 2 && t.dist && t.center) {
      const dx = ts[0].x - ts[1].x, dy = ts[0].y - ts[1].y;
      const newDist = Math.hypot(dx, dy);
      const scale = t.dist / newDist; // >1 zoom out, <1 zoom in
      const cx = t.vx.xMin + (t.center.x / size.w) * (t.vx.xMax - t.vx.xMin);
      const cy = t.vx.yMin + ((size.h - t.center.y) / size.h) * (t.vx.yMax - t.vx.yMin);
      setViewport({
        xMin: cx - (cx - t.vx.xMin) * scale,
        xMax: cx + (t.vx.xMax - cx) * scale,
        yMin: cy - (cy - t.vx.yMin) * scale,
        yMax: cy + (t.vx.yMax - cy) * scale,
      });
    }
  };
  const onTouchEnd = () => { touchRef.current = null; };

  // Click trace
  const onClick = (e: React.MouseEvent) => {
    if (dragRef.current) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const x = pxToX(px);
    // pick nearest curve y at that x
    let best: { y: number; dy: number; color: string } | null = null;
    for (const s of sampled) {
      if (!s.fn.ok || !s.row.visible || !s.points.length) continue;
      // binary search-ish (linear okay for n=500)
      let nearest = s.points[0];
      let nd = Math.abs(nearest.x - x);
      for (const p of s.points) {
        const d = Math.abs(p.x - x);
        if (d < nd) { nd = d; nearest = p; }
      }
      if (!nearest.defined) continue;
      const dy = Math.abs(yToPx(nearest.y) - py);
      if (best === null || dy < best.dy) best = { y: nearest.y, dy, color: s.row.color };
    }
    if (best && best.dy < 30) {
      setTracePoints([...tracePoints, { x, y: best.y, color: best.color }]);
    }
  };

  return (
    <div ref={wrapperRef} className="mt-4 overflow-hidden rounded-xl border border-border bg-white dark:bg-zinc-950">
      <canvas
        ref={canvasRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={onClick}
        className="block w-full cursor-crosshair touch-none select-none"
        style={{ touchAction: "none" }}
      />
    </div>
  );
}

interface DrawOpts {
  showGrid: boolean;
  showLabels: boolean;
  bnDigits: boolean;
  tracePoints: { x: number; y: number; color: string }[];
  intersections: { i: number; j: number; pts: { x: number; y: number }[] }[];
}

function niceStep(range: number): number {
  const target = range / 10;
  const exp = Math.floor(Math.log10(target));
  const base = Math.pow(10, exp);
  const mult = target / base;
  let m = 1;
  if (mult >= 5) m = 5;
  else if (mult >= 2) m = 2;
  return m * base;
}

function drawPlot(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  vp: Viewport,
  sampled: { row: FunctionRow; fn: { ok: boolean }; points: SamplePoint[] }[],
  opts: DrawOpts,
) {
  ctx.clearRect(0, 0, w, h);
  // bg
  ctx.fillStyle = "#ffffff";
  // honor dark mode via CSS class — read from body
  if (typeof document !== "undefined" && document.documentElement.classList.contains("dark")) {
    ctx.fillStyle = "#09090b";
  }
  ctx.fillRect(0, 0, w, h);

  const xRange = vp.xMax - vp.xMin;
  const yRange = vp.yMax - vp.yMin;
  if (xRange <= 0 || yRange <= 0) return;
  const xToPx = (x: number) => ((x - vp.xMin) / xRange) * w;
  const yToPx = (y: number) => h - ((y - vp.yMin) / yRange) * h;

  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const axisColor = isDark ? "#a1a1aa" : "#52525b";
  const textColor = isDark ? "#d4d4d8" : "#3f3f46";

  // Grid
  if (opts.showGrid) {
    const xStep = niceStep(xRange);
    const yStep = niceStep(yRange);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const xStart = Math.ceil(vp.xMin / xStep) * xStep;
    for (let x = xStart; x <= vp.xMax; x += xStep) {
      const px = xToPx(x);
      ctx.moveTo(px, 0); ctx.lineTo(px, h);
    }
    const yStart = Math.ceil(vp.yMin / yStep) * yStep;
    for (let y = yStart; y <= vp.yMax; y += yStep) {
      const py = yToPx(y);
      ctx.moveTo(0, py); ctx.lineTo(w, py);
    }
    ctx.stroke();

    // Labels
    if (opts.showLabels) {
      ctx.fillStyle = textColor;
      ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
      ctx.textBaseline = "top";
      for (let x = xStart; x <= vp.xMax; x += xStep) {
        if (Math.abs(x) < xStep / 2) continue;
        const px = xToPx(x);
        const py = Math.min(h - 14, Math.max(2, yToPx(0) + 2));
        let lbl = formatTick(x, xStep);
        if (opts.bnDigits) lbl = toBengaliNumerals(lbl);
        ctx.fillText(lbl, px + 2, py);
      }
      ctx.textBaseline = "middle";
      for (let y = yStart; y <= vp.yMax; y += yStep) {
        if (Math.abs(y) < yStep / 2) continue;
        const py = yToPx(y);
        const px = Math.min(w - 24, Math.max(2, xToPx(0) + 4));
        let lbl = formatTick(y, yStep);
        if (opts.bnDigits) lbl = toBengaliNumerals(lbl);
        ctx.fillText(lbl, px, py);
      }
    }
  }

  // Axes
  ctx.strokeStyle = axisColor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  if (vp.yMin <= 0 && vp.yMax >= 0) {
    const py = yToPx(0);
    ctx.moveTo(0, py); ctx.lineTo(w, py);
  }
  if (vp.xMin <= 0 && vp.xMax >= 0) {
    const px = xToPx(0);
    ctx.moveTo(px, 0); ctx.lineTo(px, h);
  }
  ctx.stroke();

  // Curves
  for (const s of sampled) {
    if (!s.fn.ok || !s.row.visible || !s.points.length) continue;
    const segs = toSegments(s.points, yRange);
    ctx.strokeStyle = s.row.color;
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    for (const seg of segs) {
      if (seg.length < 2) continue;
      ctx.beginPath();
      let started = false;
      for (const p of seg) {
        const py = yToPx(p.y);
        if (py < -2000 || py > h + 2000) {
          if (started) { ctx.stroke(); ctx.beginPath(); started = false; }
          continue;
        }
        const px = xToPx(p.x);
        if (!started) { ctx.moveTo(px, py); started = true; }
        else ctx.lineTo(px, py);
      }
      if (started) ctx.stroke();
    }
  }

  // Intersections markers
  ctx.fillStyle = isDark ? "#fbbf24" : "#d97706";
  for (const it of opts.intersections) {
    for (const p of it.pts) {
      if (p.x < vp.xMin || p.x > vp.xMax || p.y < vp.yMin || p.y > vp.yMax) continue;
      const px = xToPx(p.x), py = yToPx(p.y);
      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Trace points
  for (const t of opts.tracePoints) {
    if (t.x < vp.xMin || t.x > vp.xMax || t.y < vp.yMin || t.y > vp.yMax) continue;
    const px = xToPx(t.x), py = yToPx(t.y);
    ctx.fillStyle = t.color;
    ctx.beginPath();
    ctx.arc(px, py, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = isDark ? "#09090b" : "#ffffff";
    ctx.lineWidth = 2;
    ctx.stroke();
    // label
    const lblX = `(${formatTick(t.x, niceStep(xRange))}, ${formatTick(t.y, niceStep(yRange))})`;
    const label = opts.bnDigits ? toBengaliNumerals(lblX) : lblX;
    ctx.fillStyle = textColor;
    ctx.font = "11px ui-sans-serif, system-ui";
    ctx.fillText(label, px + 8, py - 8);
  }
}

function formatTick(v: number, step: number): string {
  const decimals = step >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(step)));
  return Number(v.toFixed(decimals)).toString();
}

function exportPng(canvas: HTMLCanvasElement | null) {
  if (!canvas || typeof document === "undefined") return;
  const url = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = "graph.png";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
